import { useAuthenticationContext } from "@climatepartner/app-shell/authentication-context";
import { SubTenantTreeNode } from "@climatepartner/app-shell/authentication-context";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";

export type SubAccountTreeNode = {
  accountId: string;
  accountName: string;
  children: SubAccountTreeNode[];
};

function mapSubTenantTreeNodeToSubAccountTreeNode(node: SubTenantTreeNode): SubAccountTreeNode {
  return {
    accountId: node.tenantId,
    accountName: node.tenantName,
    children: node.children.map(mapSubTenantTreeNodeToSubAccountTreeNode),
  };
}

type UseSubAccountTreeReturn = {
  tree: SubAccountTreeNode | null;
  isLoading: boolean;
};

type SubAccountTreeHookOptions = {
  isEnabled: boolean;
};

export const SUB_ACCOUNT_TREE_QUERY_KEY = ["sub-account-tree"] as const;

export function useSubAccountTree({ isEnabled }: SubAccountTreeHookOptions): UseSubAccountTreeReturn {
  const { user, actions } = useAuthenticationContext();
  // `actions` is recreated on every auth context render; the ref lets the queryFn read the
  // latest version after loadSubAccountsTree resolves and Frontegg updates its tenantTree state.
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  const { data: tree = null, isLoading } = useQuery({
    queryKey: [...SUB_ACCOUNT_TREE_QUERY_KEY, user.activeAccount.accountId],
    queryFn: async () => {
      await actionsRef.current.loadSubAccountsTree();
      // Frontegg updates its Redux state synchronously when the promise resolves, but
      // React batches the resulting re-renders. Yielding to the macrotask queue gives
      // React time to flush those re-renders so actionsRef.current reflects the new
      // tenantTree before we read it.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      const rawTree = actionsRef.current.getSubAccountsTree();
      return rawTree ? mapSubTenantTreeNodeToSubAccountTreeNode(rawTree) : null;
    },
    enabled: isEnabled,
  });

  return { tree, isLoading };
}

function flattenSubAccountTreeToAccountIds(node: SubAccountTreeNode): string[] {
  return [node.accountId, ...node.children.flatMap(flattenSubAccountTreeToAccountIds)];
}

function buildSubAccountTreeAccountPathMap(
  node: SubAccountTreeNode,
  ancestorNames: string[],
  map: Map<string, string[]>
): void {
  const currentPath = [...ancestorNames, node.accountName];
  map.set(node.accountId, currentPath);
  node.children.forEach((child) => buildSubAccountTreeAccountPathMap(child, currentPath, map));
}

/** Accounts that are not multilayer have no tree, so the active account's name comes from the auth context. */
export function useAccountPathMap({ isEnabled }: SubAccountTreeHookOptions): Map<string, string[]> {
  const { user } = useAuthenticationContext();
  const { tree } = useSubAccountTree({ isEnabled });
  const { accountId, accountName } = user.activeAccount;

  return useMemo(() => {
    if (!tree) return new Map([[accountId, [accountName]]]);
    const map = new Map<string, string[]>();
    buildSubAccountTreeAccountPathMap(tree, [], map);
    return map;
  }, [tree, accountId, accountName]);
}

export type SubAccountOwnerOption = {
  accountId: string;
  accountName: string;
};

function flattenSubAccountTreeToOwnerOptions(node: SubAccountTreeNode): SubAccountOwnerOption[] {
  return [
    { accountId: node.accountId, accountName: node.accountName },
    ...node.children.flatMap(flattenSubAccountTreeToOwnerOptions),
  ];
}

export function useFlattenedSubAccounts({ isEnabled }: SubAccountTreeHookOptions): SubAccountOwnerOption[] {
  const { tree } = useSubAccountTree({ isEnabled });

  return useMemo(() => (tree ? flattenSubAccountTreeToOwnerOptions(tree) : []), [tree]);
}

export function useSubAccountIds({ isEnabled }: SubAccountTreeHookOptions): string[] {
  const { tree } = useSubAccountTree({ isEnabled });

  return useMemo(() => (tree ? flattenSubAccountTreeToAccountIds(tree) : []), [tree]);
}
